import React, { useEffect } from "react";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";
import { Trophy, Medal } from "lucide-react";
import { TeamLogo } from "./BracketView";
import ChampionShare from "./ChampionShare";

const MEDAL = ["#facc15", "#cbd5e1", "#d97706"];

export default function ChampionBanner({ result, name, slug }) {
  useEffect(() => {
    const end = Date.now() + 1600;
    const colors = ["#facc15", "#2563eb", "#e11d48", "#ffffff"];
    const frame = () => {
      confetti({ particleCount: 4, angle: 60, spread: 60, origin: { x: 0, y: 0.6 }, colors, disableForReducedMotion: true });
      confetti({ particleCount: 4, angle: 120, spread: 60, origin: { x: 1, y: 0.6 }, colors, disableForReducedMotion: true });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, []);
  if (!result?.champion) return null;
  const logos = Object.fromEntries((result.placements || []).map((p) => [p.team, p.logo]));
  return (
    <section className="relative overflow-hidden rounded-2xl border border-yellow-400/30 bg-gradient-to-br from-yellow-500/15 via-[hsl(var(--card))] to-[hsl(var(--primary))]/15 p-6 md:p-8" data-testid="champion-banner">
      <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-yellow-400/10 blur-3xl" />
      <div className="eyebrow text-yellow-400 flex items-center gap-2"><Trophy size={14} /> Tournament Result</div>
      <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <motion.div initial={{ scale: 0.5, rotate: -10, opacity: 0 }} animate={{ scale: 1, rotate: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 180, damping: 12 }}
          className="relative p-1 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-600 shadow-[0_0_40px_rgba(250,204,21,0.35)]">
          <div className="rounded-full bg-black p-1"><TeamLogo team={result.champion} logos={logos} size="w-20 h-20 md:w-24 md:h-24 text-3xl" /></div>
        </motion.div>
        <div className="min-w-0">
          <div className="text-xs font-semibold text-white/50 uppercase tracking-widest">Champion of {name}</div>
          <motion.h2 initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="text-3xl md:text-4xl font-bold text-white break-words" data-testid="champion-name">{result.champion}</motion.h2>
        </div>
      </div>
      {result.placements?.length > 1 && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="placements">
          {result.placements.slice(0, 6).map((p, i) => (
            <motion.div key={p.team} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.06 }}
              className="flex items-center gap-2.5 rounded-xl bg-black/30 border border-white/5 px-3 py-2.5 min-w-0">
              <span className="w-7 h-7 shrink-0 rounded-full grid place-items-center text-xs font-bold" style={{ background: MEDAL[i] || "rgba(255,255,255,0.08)", color: i < 3 ? "#000" : "#fff" }}>{i < 3 ? <Medal size={14} /> : p.rank}</span>
              <TeamLogo team={p.team} logos={logos} size="w-6 h-6" />
              <span className="text-sm font-semibold text-white truncate">{p.team}</span>
            </motion.div>
          ))}
        </div>
      )}
      {slug && <ChampionShare slug={slug} champion={result.champion} name={name} />}
    </section>
  );
}
